import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import styles from './ExternalHeader.module.scss';
import biotailLogo from '../../../Assets/Images/biotail_logo.svg';
import BreedDropdown from '../../Shared/BreedDropdown/BreedDropdown';
import BreedSelectOverlay from '../BreedSelectOverlay/BreedSelectOverlay';
import { general } from '../../../general';

interface EntryUrlData {
    ID: number;
    URL_TAG: string;
    APP_BRAND_NAME: string;
    BREED_IDS: number[];
    LOGO?: string;
}

interface ExternalHeaderProps {
    /** Show breed dropdown */
    showBreedDropdown?: boolean;
    /** Available breeds for dropdown */
    breeds?: any[];
    /** Currently selected breed */
    selectedBreed?: string;
    /** Callback when breed changes */
    onBreedChange?: (breedId: string) => void;
    /** Back link configuration */
    backLink?: {
        /** Link destination */
        to: string;
        /** Link text */
        text: string;
        /** Optional icon/emoji */
        icon?: string;
        /** Open in new tab */
        openInNewTab?: boolean;
    };
}

function ExternalHeader({
    showBreedDropdown = false,
    breeds = [],
    selectedBreed = '',
    onBreedChange,
    backLink
}: ExternalHeaderProps) {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [entryUrlData, setEntryUrlData] = useState<EntryUrlData | null>(null);
    const [entryLoading, setEntryLoading] = useState(false);

    const entryTag = general.getEntryTag(searchParams);

    useEffect(() => {
        if (entryTag) {
            setEntryLoading(true);
            axios.get(`/api/entryurl/tag/${encodeURIComponent(entryTag)}`).then(res => {
                setEntryUrlData(res.data);
            }).catch(() => {
                setEntryUrlData(null);
            }).finally(() => {
                setEntryLoading(false);
            });
        } else {
            setEntryUrlData(null);
            setEntryLoading(false);
        }
    }, [entryTag]);

    const displayedBreeds = entryUrlData
        ? breeds.filter(b => entryUrlData.BREED_IDS.includes(Number(b.ID)))
        : breeds;

    const brandName = (!entryTag || entryLoading) ? (entryTag ? '' : 'Biotail AvlsPortal') : (entryUrlData?.APP_BRAND_NAME ?? 'Biotail AvlsPortal');

    const backLinkTo = backLink
        ? (backLink.to.startsWith('/external') ? backLink.to + location.search : backLink.to)
        : '';
    return (
        <>
        {showBreedDropdown && displayedBreeds.length > 0 && !selectedBreed && (
            <BreedSelectOverlay
                breeds={displayedBreeds}
                onSelect={(breedId) => onBreedChange?.(breedId)}
                logo={entryUrlData?.LOGO}
                brandName={brandName || undefined}
            />
        )}
        <div className={styles.header}>
            <div className={styles.headerLeft}>
                <Link to={`/external${location.search}`} className={styles.logoLink}>
                    {entryUrlData?.LOGO
                        ? <img src={`data:image/*;base64,${entryUrlData.LOGO}`} alt={entryUrlData.APP_BRAND_NAME} className={styles.headerLogo} />
                        : <img src={biotailLogo} alt="Biotail Logo" className={styles.headerLogo} />
                    }
                    <div className={styles.headerTextGroup}>
                        <h1 className={styles.headerTitle}>{brandName}</h1>
                        {entryUrlData?.LOGO && (
                            <span className={styles.poweredBy}>Powered by Biotail</span>
                        )}
                    </div>
                </Link>
            </div>
            
            {showBreedDropdown && (
                <div className={styles.headerCenter}>
                    <BreedDropdown
                        breeds={displayedBreeds}
                        value={selectedBreed}
                        onChange={(e) => onBreedChange?.(e.target.value)}
                        className={styles.breedDropdown}
                    />
                </div>
            )}
            
            <div className={styles.headerRight}>
                {backLink && (
                    <Link 
                        to={backLinkTo}
                        className={styles.backButton}
                        target={backLink.openInNewTab ? "_blank" : undefined}
                    >
                        <span className={styles.buttonText}>
                            {backLink.text}
                        </span>
                        {backLink.icon && (
                            <span className={styles.buttonIcon}>{backLink.icon}</span>
                        )}
                    </Link>
                )}
            </div>
            
            {showBreedDropdown && (
                <div className={styles.mobileRow}>
                    <BreedDropdown
                        breeds={displayedBreeds}
                        value={selectedBreed}
                        onChange={(e) => onBreedChange?.(e.target.value)}
                        className={styles.breedDropdown}
                    />
                    {backLink && (
                        <Link 
                            to={backLinkTo}
                            className={styles.backButton}
                            target={backLink.openInNewTab ? "_blank" : undefined}
                        >
                            <span className={styles.buttonText}>
                                {backLink.text}
                            </span>
                            {backLink.icon && (
                                <span className={styles.buttonIcon}>{backLink.icon}</span>
                            )}
                        </Link>
                    )}
                </div>
            )}
        </div>
        </>
    );
}

export default ExternalHeader;
